import { ref } from 'vue'
import {
  DocumentDetailData,
  CategoryConfig,
  DocumentConfig,
  TutorialCategoryData,
  CategoryListApiData,
  TutorialHomePageConfig
} from '~/server/types'
import type { CatalogsConfig } from '~/components/gfr/types/components'
interface TutorialHomePage {
  [key: string]: TutorialHomePageConfig
}
export const useDocumentStore = defineStore('Document', () => {
  const { config } = storeToRefs(useStore())
  const documentConfig = ref<DocumentDetailData>({
    id: -1,
    title: '',
    content: '',
    content_html: '',
    image: '',
    doc_info: []
  } as DocumentDetailData)
  const typeData = ref<CatalogsConfig[]>([]) // include obsolete documents
  const moduleData = ref<CatalogsConfig[]>([]) // include obsolete documents
  const tutorialMenu = ref<CatalogsConfig[]>([])
  const wikiMenu = ref<CatalogsConfig[]>([])
  const tutorialHomeList = ref<TutorialHomePage>({})
  const routePath = ref<CatalogsConfig[]>([])
  const nextDoc = ref<CatalogsConfig | null>(null)
  const prevDoc = ref<CatalogsConfig | null>(null)
  /* --- Document --- */
  async function fetchDocumentDetail(documentId: string, type: 'tutorial' | 'document' = 'document') {
    try {
      const res = await useFetch(`/api/${type}/detail?id=${documentId}&lang_id=${config.value.id}&p4=1`)
      documentConfig.value = res.data.value.data as DocumentDetailData
    } catch (error) {
      showError({
        statusCode: 404,
        statusMessage: 'Page Not Found'
      })
    }
  }

  async function fetchCategoryList() {
    const category = await useFetch(`/api/document/category?lang_id=${config.value.id}`)
    const typeDataList = category.data.value.data.filter((item: CategoryListApiData) => item.category_type === 1)[0]
    typeData.value = formatCategoryData(typeDataList.categories || [])
  }
  async function fetchModuleList() {
    const module = await useFetch(`/api/document/modules?lang_id=${config.value.id}`)
    moduleData.value = formatDocumentData(module.data.value.data.data || [])
  }

  function formatCategoryData(config: CategoryConfig[]) {
    const catalogs: CatalogsConfig[] = []
    config.forEach((item) => {
      const categoryDoc = filterDocument(item.documents || [], 1)[0] || ({} as DocumentConfig)
      const extraDocuments = filterDocument(item.documents || [], 0)
      const catalog: CatalogsConfig = {
        id: item.id,
        title: item.name,
        url: categoryDoc.p4_id || '',
        image: categoryDoc.image || '',
        open: false,
        obsoleted: categoryDoc.is_obsolete === 1
      }
      catalog.children = [...formatDocumentData(extraDocuments), ...formatCategoryData(item.subcategories || [])]
      catalogs.push(catalog)
    })
    return catalogs
  }
  function filterDocument(arr: DocumentConfig[], forCategory: 0 | 1) {
    return arr.filter((item) => item.for_category === forCategory)
  }
  function formatDocumentData(config: DocumentConfig[]) {
    const documents: CatalogsConfig[] = []
    config.forEach((item) => {
      const document: CatalogsConfig = {
        id: item.id,
        title: item.title,
        url: item.p4_id,
        obsoleted: item.is_obsolete === 1,
        image: item.image || '',
        open: false
      }
      documents.push(document)
    })
    return documents
  }

  /* --- Tutorial --- */
  async function fetchTutorialCategory(platform: string, categoryType: '1' | '2') {
    const category = await useFetch(
      `/api/tutorial/category?lang_id=${config.value.id}&platform=${platform}&category_type=${categoryType}`
    )
    categoryType === '2' && (wikiMenu.value = formatTutorialCategory(category.data.value.data))
    categoryType === '1' && (tutorialMenu.value = formatTutorialCategory(category.data.value.data))
  }
  /*
   * @params: dataList <TutorialCategoryData[]>
   *  @desc: 根据TutorialCategoryData[], 格式化为CatalogsConfig[]
   * */
  function formatTutorialCategory(dataList: TutorialCategoryData[]) {
    const catalogs: CatalogsConfig[] = []
    tutorialHomeList.value = {}
    dataList.forEach((data) => {
      if (data.homepage) {
        const homepageCatalog = formatHomepageToCatalog(data.homepage)
        homepageCatalog.children = formatCategoryData(data.categories)
        tutorialHomeList.value[data.homepage.id] = data.homepage
        catalogs.push(homepageCatalog)
      } else {
        catalogs.push(...formatCategoryData(data.categories))
      }
    })
    return catalogs
  }
  /*
   * @params: config <TutorialHomePageConfig>
   *  @desc: 将homepage字段的数据格式化为CatalogsConfig类型，并作为最上层目录
   * */
  function formatHomepageToCatalog(config: TutorialHomePageConfig): CatalogsConfig {
    return {
      id: config.id,
      title: config.name,
      url: String(config.p4_id),
      open: false,
      obsoleted: false,
      children: []
    }
  }

  function updateActivePath(
    arr: CatalogsConfig[],
    key: keyof CatalogsConfig,
    value: number | string,
    basePath?: CatalogsConfig,
    foundCallBack?: () => void
  ) {
    const path: CatalogsConfig[] = []
    const found = arr.some(function find(item: CatalogsConfig) {
      const bool: boolean = Boolean(item[key] === value || item.children?.some(find))
      if (bool) {
        path.push(item)
      }
      return bool
    })
    if (found) {
      foundCallBack && foundCallBack()
      path.forEach((item) => {
        item.open = true
      })
      routePath.value = basePath ? [basePath, ...path.reverse()] : path.reverse() // 反转，保证是从最上层开始的路径
    }
    return found
  }

  /* --- find next or pre document from allDocuments --- */
  function findDocument(
    arr: CatalogsConfig[],
    key: keyof CatalogsConfig,
    value: string,
    type: 'next' | 'pre'
  ): CatalogsConfig | null {
    const index = arr.findIndex((item) => item[key] === value)
    if (index === -1) return null
    return type === 'next' ? arr[index + 1] || null : arr[index - 1] || null
  }
  const setDocuments = (documents: CatalogsConfig[], key: keyof CatalogsConfig, value: string, condition: boolean) => {
    nextDoc.value = !condition ? findDocument(documents, key, value, 'next') : null
    prevDoc.value = !condition ? findDocument(documents, key, value, 'pre') : null
  }
  return {
    documentConfig,
    typeData,
    moduleData,
    tutorialMenu,
    wikiMenu,
    tutorialHomeList,
    nextDoc,
    prevDoc,
    routePath,
    fetchCategoryList,
    fetchModuleList,
    fetchDocumentDetail,
    fetchTutorialCategory,
    updateActivePath,
    setDocuments
  }
})
